import { useCallback } from 'react';

import { CallActionPayload, MethodServiceType, MethodResponse } from '../types';

interface ImpactAiTracker {
  trackMethodExecute: (
    methodName: string,
    payload: unknown,
    success: boolean,
    duration: number,
    actionIndex: number
  ) => void;
  trackFormFieldUpdate: (
    form: string,
    field: string,
    value: unknown,
    source: 'ai' | 'user'
  ) => void;
  trackFormNavigation: (url: string, section?: string) => void;
}

interface TrackedMethodServiceOptions {
  methodService: MethodServiceType;
  tracker: ImpactAiTracker;
  actionIndex: number;
}

export const useTrackedMethodService = ({
  methodService,
  tracker,
  actionIndex,
}: TrackedMethodServiceOptions): MethodServiceType => {
  const trackedMethodService: MethodServiceType = useCallback(
    async (
      methodName: string,
      payload: CallActionPayload,
      uiStateCallback?,
      retryCallback?
    ): Promise<MethodResponse | void> => {
      const startTime = Date.now();
      let success = false;

      try {
        // Handle form field tracking for set-form-fields method
        if (methodName === 'set-form-fields') {
          const { form, fields } = payload as {
            form: string;
            fields: Record<string, unknown>;
          };

          if (form && fields) {
            // Track each field update
            Object.entries(fields).forEach(([fieldName, fieldValue]) => {
              tracker.trackFormFieldUpdate(form, fieldName, fieldValue, 'ai');
            });
          }
        }

        // Handle navigation tracking for navigate method
        if (methodName === 'navigate') {
          const { url } = payload as { url: string };
          if (url) {
            const section = url.includes('#') ? url.split('#')[1] : undefined;
            tracker.trackFormNavigation(url, section);
          }
        }

        // Execute the actual method
        const response = await methodService(methodName, payload, uiStateCallback, retryCallback);
        success = true;
        return response;
      } catch (error) {
        success = false;
        throw error;
      } finally {
        // Track method execution
        const duration = Date.now() - startTime;
        tracker.trackMethodExecute(methodName, payload, success, duration, actionIndex);
      }
    },
    [methodService, tracker, actionIndex]
  );

  return trackedMethodService;
};
